import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

export const getExpertises = createAsyncThunk(
    "expertises/getExpertises",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${API_URL}/expertises`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.response?.data || 'Request failed');
        }
    }
);

export const createExpertise = createAsyncThunk(
    "expertises/createExpertise",
    async (data, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.post(`${API_URL}/expertises`, data, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.response?.data || 'Request failed');
        }
    }
);

export const getActiveExpertises = createAsyncThunk(
    "expertises/getActiveExpertises",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${API_URL}/expertises/active`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.response?.data || 'Request failed');
        }
    }
);

export const getExpertiseById = createAsyncThunk(
    "expertises/getExpertiseById",
    async (id, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${API_URL}/expertises/${id}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.response?.data || 'Request failed');
        }
    }
);

export const updateExpertise = createAsyncThunk(
    "expertises/updateExpertise",
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.put(`${API_URL}/expertises/${id}`, data, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.response?.data || 'Request failed');
        }
    }
);

export const deleteExpertise = createAsyncThunk(
    "expertises/deleteExpertise",
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.delete(`${API_URL}/expertises/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.response?.data || 'Request failed');
        }
    }
);

export const getExpertiseBySlug = createAsyncThunk(
    "expertises/getExpertiseBySlug",
    async (slug, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${API_URL}/expertises/slug/${slug}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.response?.data || 'Request failed');
        }
    }
);

const expertisesSlice = createSlice({
    name: "expertises",
    initialState: {
        expertises: [],
        loading: false,
        error: null,
        expertiseDetail: null,
    },
    reducers: {
        clearExpertisesError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getExpertises.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getExpertises.fulfilled, (state, action) => {
                const list = Array.isArray(action.payload?.data)
                    ? action.payload.data
                    : Array.isArray(action.payload)
                        ? action.payload
                        : [];
                state.expertises = list;
                state.loading = false;
                state.error = null;
            })
            .addCase(getExpertises.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            .addCase(createExpertise.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createExpertise.fulfilled, (state, action) => {
                const created = action.payload?.data || action.payload;
                const list = Array.isArray(state.expertises) ? state.expertises : [];
                state.expertises = [...list, created];
                state.loading = false;
                state.error = null;
            })
            .addCase(createExpertise.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            .addCase(getActiveExpertises.fulfilled, (state, action) => {
                const list = Array.isArray(action.payload?.data) ? action.payload.data : Array.isArray(action.payload) ? action.payload : [];
                state.expertises = list;
                state.loading = false;
                state.error = null;
            })

            .addCase(getExpertiseById.fulfilled, (state, action) => {
                state.expertiseDetail = action.payload;
                state.loading = false;
                state.error = null;
            })
            .addCase(getExpertiseById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            .addCase(updateExpertise.fulfilled, (state, action) => {
                const updated = action.payload?.data || action.payload;
                const list = Array.isArray(state.expertises) ? state.expertises : [];
                state.expertises = list.map((e) => (e._id === updated._id ? updated : e));
                state.loading = false;
                state.error = null;
            })
            .addCase(updateExpertise.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            .addCase(deleteExpertise.fulfilled, (state, action) => {
                const id = action.meta.arg;
                const list = Array.isArray(state.expertises) ? state.expertises : [];
                state.expertises = list.filter((e) => e._id !== id);
                state.loading = false;
                state.error = null;
            })
            .addCase(deleteExpertise.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            .addCase(getExpertiseBySlug.fulfilled, (state, action) => {
                state.expertiseDetail = action.payload;
                state.loading = false;
                state.error = null;
            })
            .addCase(getExpertiseBySlug.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export const { clearExpertisesError } = expertisesSlice.actions;

export default expertisesSlice.reducer;
